/**
 * API client utility
 */

import {
  CreateJoinRequestResponse,
  ListJoinRequestsResponse,
  PollJoinRequestResponse,
  PresignRequest,
  PresignResponse,
  SessionAccess,
  SessionJoinRequest,
  SessionJoinResponse,
  Track,
  TrackCommit,
  SessionUserSummary,
} from "@jamroom/shared";
import { buildApiUrl } from "../config";

export interface SessionResponse {
  id: string;
  title: string;
  tracks: Track[];
  usersOnline?: number;
  createdAt: string;
  expiresAt?: string;
  isPrivate?: boolean;
}

export interface HealthResponse {
  ok: boolean;
  env: "dev" | "prod";
}

export interface MeResponse {
  email: string | null;
  role: "OWNER" | "USER";
  status: "PENDING" | "APPROVED" | "BANNED";
  smokeMode?: boolean;
}

export interface AdminUsersResponse {
  users: Array<{
    id: number;
    email: string;
    sub: string;
    role: "OWNER" | "USER";
    status: "PENDING" | "APPROVED" | "BANNED";
    createdAt: string;
    updatedAt: string;
  }>;
}

export interface AdminAudioObject {
  key: string;
  size: number;
  etag?: string;
  uploaded?: string;
}

export interface AdminAudioListResponse {
  objects: AdminAudioObject[];
  cursor: string | null;
  truncated: boolean;
}

export interface AdminActiveSessionItem {
  sessionId: string;
  title: string;
  createdAt: string;
  expiresAt: string;
  isPrivate: boolean;
  requiresApproval: boolean;
  usersOnline: number;
  updatedAt: string;
  status: "ACTIVE" | "EXPIRED";
}

export interface AdminActiveSessionsResponse {
  sessions: AdminActiveSessionItem[];
  serverNow?: string;
}

export interface SessionAdminUsersResponse {
  users: SessionUserSummary[];
  serverNow?: string;
}

class ApiClient {
  private sessionToken: string | null = null;
  private authToken: string | null = null;

  setSessionToken(token: string | null): void {
    this.sessionToken = token;
  }

  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  private isCrossOrigin(urlString: string): boolean {
    if (typeof window === "undefined") return false;
    try {
      const u = new URL(urlString, window.location.origin);
      return u.origin !== window.location.origin;
    } catch {
      return false;
    }
  }

  private withSessionTokenQuery(urlString: string, token: string): string {
    const trimmed = String(token || "").trim();
    if (!trimmed) return urlString;
    try {
      const base = typeof window === "undefined" ? "http://localhost" : window.location.origin;
      const u = new URL(urlString, base);
      u.searchParams.set("token", trimmed);
      return u.toString();
    } catch {
      return urlString;
    }
  }

  private shouldSendAuthToken(path: string): boolean {
    const p = String(path || "");
    if (!this.authToken) return false;
    if (p === "/api/me") return true;
    if (p === "/api/sessions") return true; // create session (owner-only in non-smoke)
    if (p.startsWith("/api/admin/")) return true;
    if (p.startsWith("/auth/")) return true;
    return false;
  }

  private buildHeaders(
    headers?: HeadersInit,
    opts?: {
      includeJsonContentType?: boolean;
      includeAuthToken?: boolean;
      includeSessionToken?: boolean;
    }
  ): Headers {
    const resolved = new Headers(headers);
    const includeJsonContentType = opts?.includeJsonContentType !== false;
    const includeAuthToken = opts?.includeAuthToken !== false;
    const includeSessionToken = opts?.includeSessionToken !== false;

    if (includeJsonContentType && !resolved.has("Content-Type")) {
      resolved.set("Content-Type", "application/json");
    }
    if (includeAuthToken && this.authToken && !resolved.has("Authorization")) {
      resolved.set("Authorization", `Bearer ${this.authToken}`);
    }
    if (includeSessionToken && this.sessionToken) {
      resolved.set("X-Jamroom-Token", this.sessionToken);
    }
    return resolved;
  }

  async fetch<T>(
    path: string,
    options?: RequestInit
  ): Promise<T> {
    const method = String(options?.method || "GET").toUpperCase();
    const isSessionScoped = path.startsWith("/api/sessions/");
    const baseUrl = buildApiUrl(path);
    const isCrossOrigin = this.isCrossOrigin(baseUrl);

    // Reduce CORS preflight noise for cross-origin session-scoped calls:
    // - prefer `?token=...` over `X-Jamroom-Token`
    // - avoid forcing `Content-Type: application/json` (browser will default to text/plain for string bodies)
    const useTokenQuery = Boolean(isSessionScoped && isCrossOrigin && this.sessionToken);
    const url = useTokenQuery ? this.withSessionTokenQuery(baseUrl, this.sessionToken!) : baseUrl;

    const includeAuthToken = this.shouldSendAuthToken(path);
    const includeSessionTokenHeader = Boolean(isSessionScoped && this.sessionToken && !useTokenQuery);

    const avoidJsonContentType = Boolean(isSessionScoped && isCrossOrigin);
    const includeJsonContentType =
      !avoidJsonContentType && method !== "GET" && method !== "HEAD" && typeof options?.body === "string";

    const response = await fetch(url, {
      credentials: "include",
      headers: this.buildHeaders(options?.headers, {
        includeJsonContentType,
        includeAuthToken,
        includeSessionToken: includeSessionTokenHeader,
      }),
      ...options,
    });

    if (!response.ok) {
      const contentType = response.headers.get("Content-Type") || "";
      const bodyText = await response.text();

      // Avoid leaking detailed auth errors to the UI.
      if (
        response.status === 401 &&
        (bodyText.includes("Cloudflare Access") ||
          bodyText.includes("Cf-Access") ||
          bodyText.includes("Access identity") ||
          bodyText.includes("JWT") ||
          contentType.includes("text/html"))
      ) {
        throw new Error("Sign in required");
      }

      if (contentType.includes("application/json")) {
        try {
          const parsed = JSON.parse(bodyText);
          const msg = parsed?.error ? String(parsed.error) : bodyText;
          throw new Error(`API error: ${response.status} ${msg}`);
        } catch {
          // fall through
        }
      }
      throw new Error(`API error: ${response.status} ${bodyText}`);
    }

    const okContentType = response.headers.get("Content-Type") || "";
    if (!okContentType.includes("application/json")) {
      // Common when Cloudflare Access redirects to an HTML login page.
      throw new Error("Sign in required");
    }

    return response.json();
  }

  async health(): Promise<HealthResponse> {
    return this.fetch<HealthResponse>("/api/health", {
      method: "GET",
    });
  }

  async me(): Promise<MeResponse> {
    return this.fetch<MeResponse>("/api/me", { method: "GET" });
  }

  async adminListUsers(): Promise<AdminUsersResponse> {
    return this.fetch<AdminUsersResponse>("/api/admin/users", { method: "GET" });
  }

  async adminApproveUser(id: number): Promise<{ success: boolean }> {
    return this.fetch(`/api/admin/users/${id}/approve`, { method: "POST" });
  }

  async adminRejectUser(id: number): Promise<{ success: boolean }> {
    return this.fetch(`/api/admin/users/${id}/reject`, { method: "POST" });
  }

  async adminBanUser(id: number): Promise<{ success: boolean }> {
    return this.fetch(`/api/admin/users/${id}/ban`, { method: "POST" });
  }

  async adminListAudio(params?: { prefix?: string; cursor?: string; limit?: number }): Promise<AdminAudioListResponse> {
    const qs = new URLSearchParams();
    if (params?.prefix) qs.set("prefix", params.prefix);
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (typeof params?.limit === "number") qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.fetch<AdminAudioListResponse>(`/api/admin/audio${suffix}`, { method: "GET" });
  }

  async adminDeleteAudio(keys: string[]): Promise<{ success: boolean; deleted: number }> {
    return this.fetch(`/api/admin/audio/delete`, {
      method: "POST",
      body: JSON.stringify({ keys }),
    });
  }

  async adminDeleteSession(sessionId: string): Promise<{ success: boolean }> {
    const encoded = encodeURIComponent(sessionId);
    return this.fetch(`/api/admin/sessions/${encoded}`, { method: "DELETE" });
  }

  async adminListActiveSessions(): Promise<AdminActiveSessionsResponse> {
    return this.fetch<AdminActiveSessionsResponse>("/api/admin/sessions/active", { method: "GET" });
  }

  async adminExtendSession(sessionId: string, hoursToAdd: number): Promise<{ success: boolean; expiresAt: string; serverNow?: string }> {
    return this.fetch(`/api/admin/sessions/${encodeURIComponent(sessionId)}/extend`, {
      method: "POST",
      body: JSON.stringify({ hoursToAdd }),
    });
  }

  async adminListSessionUsers(sessionId: string): Promise<SessionAdminUsersResponse> {
    return this.fetch(`/api/admin/sessions/${encodeURIComponent(sessionId)}/users`, { method: "GET" });
  }

  async adminSessionUserAction(
    sessionId: string,
    action: "add" | "approve" | "reapprove" | "remove",
    displayName: string
  ): Promise<{ success: boolean; user?: SessionUserSummary; serverNow?: string }> {
    return this.fetch(`/api/admin/sessions/${encodeURIComponent(sessionId)}/users/${action}`, {
      method: "POST",
      body: JSON.stringify({ displayName }),
    });
  }

  // Sessions
  async createSession(
    baseBlob: Blob,
    title: string,
    options?: {
      visibility?: "public" | "private";
      joinCode?: string;
      expiresInHours?: number;
      isPrivate?: boolean;
    }
  ): Promise<{
    sessionId: string;
    ownerToken?: string;
    joinCode?: string;
    expiresAt?: string;
  }> {
    const formData = new FormData();
    formData.append("baseAudio", baseBlob);
    formData.append("title", title);
    if (options?.visibility) {
      formData.append("visibility", options.visibility);
    }
    if (options?.isPrivate) {
      formData.append("isPrivate", "true");
    }
    if (options?.joinCode) {
      formData.append("joinCode", options.joinCode);
    }
    if (typeof options?.expiresInHours === "number") {
      formData.append("expiresInHours", String(options.expiresInHours));
    }

    const response = await fetch(buildApiUrl("/api/sessions"), {
      method: "POST",
      body: formData,
      credentials: "include",
      headers: this.buildHeaders(undefined, {
        includeJsonContentType: false,
        includeAuthToken: true,
        includeSessionToken: false,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API error: ${response.status} ${error}`);
    }

    return response.json();
  }

  async getSession(sessionId: string): Promise<SessionResponse> {
    return this.fetch(`/api/sessions/${sessionId}`);
  }

  async getSessionAccess(sessionId: string): Promise<SessionAccess> {
    return this.fetch(`/api/sessions/${sessionId}/access`);
  }

  async joinSession(
    sessionId: string,
    payload: SessionJoinRequest & { ownerToken?: string }
  ): Promise<SessionJoinResponse> {
    return this.fetch(`/api/sessions/${sessionId}/join`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Waiting room join requests
  async createJoinRequest(sessionId: string, payload: SessionJoinRequest): Promise<CreateJoinRequestResponse> {
    return this.fetch(`/api/sessions/${sessionId}/join-requests`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async pollJoinRequest(sessionId: string, requestId: string): Promise<PollJoinRequestResponse> {
    return this.fetch(`/api/sessions/${sessionId}/join-requests/${requestId}`, { method: "GET" });
  }

  async listJoinRequests(sessionId: string): Promise<ListJoinRequestsResponse> {
    return this.fetch(`/api/sessions/${sessionId}/join-requests`, { method: "GET" });
  }

  async approveJoinRequest(sessionId: string, requestId: string): Promise<{ success: boolean }> {
    return this.fetch(`/api/sessions/${sessionId}/join-requests/${requestId}/approve`, { method: "POST" });
  }

  async denyJoinRequest(sessionId: string, requestId: string): Promise<{ success: boolean }> {
    return this.fetch(`/api/sessions/${sessionId}/join-requests/${requestId}/deny`, { method: "POST" });
  }

  // Presign upload
  async presignTrackUpload(
    sessionId: string,
    request: PresignRequest
  ): Promise<PresignResponse> {
    return this.fetch(`/api/sessions/${sessionId}/tracks/presign`, {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  // Commit (finalize) upload
  async commitTrack(
    sessionId: string,
    trackId: string,
    commit: TrackCommit
  ): Promise<{ success: boolean; track?: Track }> {
    return this.fetch(`/api/sessions/${sessionId}/tracks/${trackId}/commit`, {
      method: "POST",
      body: JSON.stringify(commit),
    });
  }

  async deleteTrack(sessionId: string, trackId: string): Promise<{ success: boolean }> {
    return this.fetch(`/api/sessions/${sessionId}/tracks/${trackId}`, {
      method: "DELETE",
    });
  }

  getTrackAudioUrl(sessionId: string, trackId: string): string {
    const encodedSessionId = encodeURIComponent(sessionId);
    const encodedTrackId = encodeURIComponent(trackId);
    return buildApiUrl(`/api/sessions/${encodedSessionId}/audio/${encodedTrackId}`);
  }

  async fetchTrackAudio(sessionId: string, trackId: string): Promise<Blob> {
    const baseUrl = this.getTrackAudioUrl(sessionId, trackId);
    const useTokenQuery = Boolean(this.sessionToken && this.isCrossOrigin(baseUrl));
    const url = useTokenQuery ? this.withSessionTokenQuery(baseUrl, this.sessionToken!) : baseUrl;

    const response = await fetch(url, {
      credentials: "include",
      headers: this.buildHeaders(undefined, {
        includeJsonContentType: false,
        includeAuthToken: false,
        includeSessionToken: Boolean(this.sessionToken && !useTokenQuery),
      }),
    });

    if (!response.ok) {
      throw new Error(`Audio fetch failed: ${response.status}`);
    }

    return response.blob();
  }

  // Upload to presigned URL
  async uploadToPresignedUrl(url: string, blob: Blob): Promise<void> {
    // Use POST multipart/form-data to avoid CORS preflight issues with some Cloudflare Access setups.
    // The server accepts both PUT (raw) and POST (multipart) for backward compatibility.
    const resolvedUrl = this.sessionToken ? this.withSessionTokenQuery(url, this.sessionToken) : url;
    const formData = new FormData();
    formData.append("file", blob, "track");

    const response = await fetch(resolvedUrl, {
      method: "POST",
      body: formData,
      credentials: "include",
      headers: this.buildHeaders(undefined, {
        includeJsonContentType: false,
        includeAuthToken: false,
        includeSessionToken: false,
      }),
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }
  }

  async deleteSession(sessionId: string): Promise<{ success: boolean }> {
    return this.fetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: "DELETE",
    });
  }

  async extendSession(sessionId: string, hoursToAdd: number): Promise<{ success: boolean; expiresAt: string; serverNow?: string }> {
    return this.fetch(`/api/sessions/${encodeURIComponent(sessionId)}/extend`, {
      method: "POST",
      body: JSON.stringify({ hoursToAdd }),
    });
  }

  async listSessionUsers(sessionId: string): Promise<SessionAdminUsersResponse> {
    return this.fetch(`/api/sessions/${encodeURIComponent(sessionId)}/admin/users`, { method: "GET" });
  }

  async sessionUserAction(
    sessionId: string,
    action: "add" | "approve" | "reapprove" | "remove",
    displayName: string
  ): Promise<{ success: boolean; user?: SessionUserSummary; serverNow?: string }> {
    return this.fetch(`/api/sessions/${encodeURIComponent(sessionId)}/admin/users/${action}`, {
      method: "POST",
      body: JSON.stringify({ displayName }),
    });
  }
}

export const apiClient = new ApiClient();
